import { MarketStatus } from '../../../apis/v2/market/types';

export const TradeEvent = {
  TradePageView: 'trade_page_view',
  TabClick: 'trade_tab_click',
  TabPriceClearClick: 'trade_tab_price_clear_click',
  TabPriceEditClick: 'trade_tab_price_edit_click',
  TabAmountResetClick: 'trade_tab_amount_reset_click',
  TpSlClick: 'trade_tab_tpsl_setting_click',
  TpslSettingView: 'tpsl_setting_view',
  TpslCurrentPriceClick: 'tpsl_current_price_click',
  TpSlPriceClearClick: 'tpsl_price_clear_click',
  TpSlPriceEditClick: 'tpsl_price_edit_click',
  TpSlPriceResetClick: 'tpsl_price_reset_click',
  TpSlSettingConfirmClick: 'tpsl_setting_confirm_click',
  TpslRatioSliderClick: 'tpsl_ratio_slider_click',
  TpslPartialBottomSheetView: 'tpsl_partial_bottom_sheet_view',
  TpslPartialBottomSheetShowClick: 'tpsl_partial_bottom_sheet_show_click',
  TpslPartialBottomSheetConfirmClick: 'tpsl_partial_bottom_sheet_confirm_click',

  // TPSL Revamp
  TpslSettingTabClick: 'tpsl_setting_tab_click',
  TpslSettingInfoClick: 'tpsl_setting_info_click',
  TpslSettingPartialSlotClick: 'tpsl_setting_partial_slot_click',
  TpslRemoveDialogView: 'tpsl_remove_dialog_view',
  TpslRemoveDialogClick: 'tpsl_remove_dialog_click',
  TpslPreferenceBottomsheetView: 'tpsl_preference_bottomsheet_view',
  OrderPreviewEnableSlClick: 'order_preview_enable_sl_click',

  OrderClick: 'trade_order_click', // only spot
  OrderReduceOnlyClick: 'order_reduce_only_click',
  MarketOrderSlippageClick: 'market_order_slippage_click', // ERD-233: Max slippage adoption / mode mix
  OrderFormSignInClick: 'order_form_sign_in_click',
  TriggerOrderTypeSelectClick: 'trigger_order_type_select_click',
  BuyCryptoClick: 'buy_crypto_click', // replace trade_order_click, only perp
  PendingTimeTriggerTpslSettingClick: 'pending_time_trigger_tpsl_setting_click',
  PendingTimeTriggerTpslConfirmClick: 'pending_time_trigger_tpsl_confirm_click',
  PendingTimeTriggerTpslDeleteClick: 'pending_time_trigger_tpsl_delete_click',
  PendingTimeTriggerTpslDeleteConfirmClick: 'pending_time_trigger_tpsl_delete_confirm_click',

  TradingOnboardingTooltipView: 'trading_onboarding_tooltip_view',
  TradingOnboardingTooltipClick: 'trading_onboarding_tooltip_click',
  TradingOnboardingTooltipCloseClick: 'trading_onboarding_tooltip_close_click',
  TradingOnboardingPageView: 'trading_onboarding_page_view',
  TradingOnboardingButtonClick: 'trading_onboarding_button_click',
  TradingOnboardingLinkClick: 'trading_onboarding_link_click',
  TradingOnboardingExitClick: 'trading_onboarding_exit_click',
  /**
   * @deprecated
   */
  TradingOnboardingNextClick: 'trading_onboarding_next_click',
  /**
   * @deprecated
   */
  TradingOnboardingStartNowClick: 'trading_onboarding_start_now_click',

  TradeSettingsClick: 'trade_settings_click',
  LeverageLockedClick: 'leverage_locked_click',
  TpslLockedClick: 'tpsl_locked_click',

  TradeErrorPopupView: 'trade_error_pop_up_view',

  TradeSearchClick: 'trade_search_click',
  SearchSelectionsView: 'search_selections_view',
  SearchSeeAllClick: 'search_see_all_click',
  SearchTypeClick: 'search_type_click',
  TradeCryptoClick: 'trade_crypto_click',
  FavoriteClick: 'favorite_click',
  EmptySearchResultPageView: 'empty_search_result_page_view',

  SelectPercentageClick: 'select_percentage_click',

  OrderFormUnitSwitchClick: 'order_form_unit_switch_click',
  OrderAmountEditClick: 'order_amount_edit_click',
  OrderBboClick: 'order_bbo_click',

  AddPositionMethodSelectClick: 'add_position_method_select_click',
  AddPositionReviewClick: 'add_position_review_click',
  AddPositionConfirmClick: 'add_position_confirm_click',

  OrderBookUnitDropdownClick: 'order_book_unit_dropdown_click',
  OrderBookUnitSelectClick: 'order_book_unit_select_click',

  OrderFormErrorMsgView: 'order_form_error_msg_view',
  OrderFormErrorMsgClick: 'order_form_error_msg_click',

  OrderFormHistoryCurrentMarketClick: 'order_form_history_current_market_click',
  OrderFormHistoryCalendarClick: 'order_form_history_calendar_click',

  TradFiOrderFormBannerView: 'tradfi_order_form_banner_view',
  TradFiOrderFormBannerClick: 'tradfi_order_form_banner_click',
} as const;

type TabTypeTypes = 'market' | 'trigger' | 'limit';
type SpotSideTabTypeTypes = 'Buy' | 'Sell';

type TimeTriggerTpslTypes = {
  symbol: string;
  side: 'buy' | 'sell';
  leverage: number;
  tpsl_type: 'Take Profit' | 'Stop Loss';
  trigger_time: string;
  trigger_price_ratio?: string;
  amount_usdt?: string;
  quantity?: string;
};

export interface TpslEventCommonPayload {
  symbol: string;
  side: 'buy' | 'sell';
  leverage: number;
  tpsl_type: 'Take Profit' | 'Stop Loss';
  order_type: 'market_order' | 'trigger_price_order' | 'trigger_time_order' | 'limit_order';
}

export interface TpslEventPayload extends TpslEventCommonPayload {
  quantity: number;
  from_entry_percentage: number;
  estimated_pnl: number;
  entry_price: number;
  trigger_price: number;
  partial_ratio: number;
}

export interface TpslPartialBottomSheetEventPayload extends TpslEventCommonPayload {
  is_show?: boolean;
}

export interface TradeEventDataType {
  [TradeEvent.TradePageView]: {
    symbol: string;
    tab_type: TabTypeTypes;
    market_status: MarketStatus | null;
  };
  [TradeEvent.TradFiOrderFormBannerView]: {
    segment_id: string;
    image_name: string;
  };
  [TradeEvent.TradFiOrderFormBannerClick]: {
    segment_id: string;
    image_name: string;
    button_url: string;
  };
  [TradeEvent.TabClick]: {
    symbol: string;
    tab_type: TabTypeTypes | SpotSideTabTypeTypes;
  };
  [TradeEvent.TabPriceClearClick]: {
    position_mode: 'oneway' | 'multiposition';
    symbol: string;
    leverage: number;
    order_type: 'market_order' | 'trigger_price_order' | 'trigger_time_order' | 'limit_order';
    order_by_token: 'position_size' | 'margin' | 'quantity';
    set_price: number;
  };
  [TradeEvent.TabPriceEditClick]: {
    position_mode: 'oneway' | 'multiposition';
    symbol: string;
    leverage: number;
    order_type: 'market_order' | 'trigger_price_order' | 'trigger_time_order' | 'limit_order';
    order_by_token: 'position_size' | 'margin' | 'quantity';
    set_price: number;
  };
  [TradeEvent.TabAmountResetClick]: {
    position_mode: 'oneway' | 'multiposition';
    symbol: string;
    leverage: number;
    order_type: 'market_order' | 'trigger_price_order' | 'trigger_time_order' | 'limit_order';
    order_by_token: 'position_size' | 'margin' | 'quantity';
    reset_amount: number;
    reset_amount_percentage: number;
  };
  [TradeEvent.TpSlClick]: {
    symbol: string;
    tab_type: TabTypeTypes;
  };
  [TradeEvent.TpslSettingView]: TpslEventPayload & { tab?: 'Entire' | 'Partial' };
  [TradeEvent.TpslCurrentPriceClick]: TpslEventPayload;
  [TradeEvent.TpSlPriceClearClick]: TpslEventPayload;
  [TradeEvent.TpSlPriceEditClick]: TpslEventPayload;
  [TradeEvent.TpSlPriceResetClick]: TpslEventPayload;
  [TradeEvent.TpSlSettingConfirmClick]: TpslEventPayload;
  [TradeEvent.TpslRatioSliderClick]: TpslEventPayload;
  [TradeEvent.TpslPartialBottomSheetView]: TpslPartialBottomSheetEventPayload;
  [TradeEvent.TpslPartialBottomSheetShowClick]: TpslPartialBottomSheetEventPayload;
  [TradeEvent.TpslPartialBottomSheetConfirmClick]: TpslPartialBottomSheetEventPayload;

  // TPSL Revamp
  [TradeEvent.TpslSettingTabClick]: {
    view_log_name: 'tpsl_setting_view';
    view_title: 'Take Profit' | 'Stop Loss';
    button_type: 'Entire' | 'Partial';
  };
  [TradeEvent.TpslSettingInfoClick]: {
    view_log_name: 'tpsl_setting_view';
    view_title: 'Take Profit' | 'Stop Loss';
    tab: 'Entire' | 'Partial';
  };
  [TradeEvent.TpslSettingPartialSlotClick]: {
    view_log_name: string;
    view_title: 'Take Profit' | 'Stop Loss';
    button_type: 'Modify' | 'Remove';
  };
  [TradeEvent.TpslRemoveDialogView]: {
    title: string;
  };
  [TradeEvent.TpslRemoveDialogClick]: {
    view_log_name: 'tpsl_remove_dialog_view';
    view_title: 'Take Profit' | 'Stop Loss';
    button_type: 'Cancel' | 'Confirm';
  };
  [TradeEvent.TpslPreferenceBottomsheetView]: {
    title: string;
  };
  [TradeEvent.OrderPreviewEnableSlClick]: {
    view_log_name: 'order_preview_view';
    enabled: boolean;
  };

  [TradeEvent.OrderReduceOnlyClick]: {
    symbol: string;
    order_type: 'market_order' | 'trigger_price_order' | 'trigger_time_order' | 'limit_order';
    toggle_on: boolean;
  };
  [TradeEvent.MarketOrderSlippageClick]: {
    symbol: string;
    toggle_on: boolean;
    mode: 'percent' | 'price';
  };
  [TradeEvent.OrderClick]: {
    trade_type: 'spot';
    symbol: string;
    order_type: TabTypeTypes;
    side: 'buy' | 'sell';
    amount: number | null;
    amount_usdt: number;
    available_funds: string;
    available_pair: number;
  };
  [TradeEvent.OrderFormSignInClick]: {
    symbol: string;
  };
  [TradeEvent.TriggerOrderTypeSelectClick]: {
    symbol: string;
    trigger_type: 'trigger_price' | 'trigger_time';
  };
  [TradeEvent.BuyCryptoClick]: {
    symbol: string;
    side: 'buy' | 'sell';
    order_type: 'market_order' | 'trigger_price_order' | 'trigger_time_order' | 'limit_order';
    leverage: number;
    amount_usdt: number;
    amount: number | null;
    review_price: number | null; // market order인 경우 estimated_price, trigger order인 경우 trigger_price를 보냄
    trigger_time: string | null;
    is_reduce_only: boolean;
    order_by_token: 'position_size' | 'margin' | 'quantity';
    position_mode: 'oneway' | 'multiposition';
    available_funds: string;
    trade_type: 'perpetual';
    margin_mode: 'Isolated margin' | 'Cross margin';
    bbo_price: string | null;
  };
  [TradeEvent.PendingTimeTriggerTpslSettingClick]: TimeTriggerTpslTypes;
  [TradeEvent.PendingTimeTriggerTpslConfirmClick]: Omit<TimeTriggerTpslTypes, 'side'> & {
    current_price: string;
    estimated_pnl: string;
    position: 'buy' | 'sell';
  };
  [TradeEvent.PendingTimeTriggerTpslDeleteClick]: Omit<TimeTriggerTpslTypes, 'leverage'>;
  [TradeEvent.PendingTimeTriggerTpslDeleteConfirmClick]: {
    symbol: string;
    tpsl_type: 'Take Profit' | 'Stop Loss';
    side: 'buy' | 'sell';
    trigger_time: string;
    trigger_price_ratio: string;
    quantity?: string;
    amount_usdt?: string;
  };

  [TradeEvent.TradingOnboardingTooltipView]: {
    tooltip_text: string;
  };
  [TradeEvent.TradingOnboardingTooltipClick]: {
    tooltip_text: string;
  };
  [TradeEvent.TradingOnboardingTooltipCloseClick]: {
    tooltip_text: string;
  };
  [TradeEvent.TradingOnboardingNextClick]: {
    onboarding_page_num: number;
  };
  [TradeEvent.TradingOnboardingPageView]: {
    title: string;
    onboarding_page_num: number;
  };
  [TradeEvent.TradingOnboardingButtonClick]: {
    title: string;
    onboarding_page_num: number;
    button_text: string;
  };
  [TradeEvent.TradingOnboardingLinkClick]: {
    title: string;
    onboarding_page_num: number;
    link_text: string;
  };
  [TradeEvent.TradingOnboardingExitClick]: {
    title: string;
    onboarding_page_num: number;
  };

  [TradeEvent.TradeSettingsClick]: {
    symbol: string;
    order_type: TabTypeTypes;
    trade_type: 'perpetual'; // trade mode(one way / multi position)설정은 perpetual에서만 가능
    is_popup: boolean;
  };
  [TradeEvent.LeverageLockedClick]: {
    symbol: string;
    leverage: number;
  };
  [TradeEvent.TpslLockedClick]: {
    symbol: string;
    leverage: number;
  };
  [TradeEvent.TradeErrorPopupView]: {
    message_text: string | null;
  };

  [TradeEvent.TradeSearchClick]: {
    trade_type: 'perpetual' | 'spot';
    symbol: string;
  };
  [TradeEvent.SearchSeeAllClick]: {
    trade_type: 'perpetual' | 'spot';
    search_word: string | null;
  };
  [TradeEvent.SearchTypeClick]: {
    search_type:
      | 'All'
      | 'Perpetual'
      | 'Spot'
      | 'Favorites'
      | 'TradFi'
      | 'Positions opened'
      | 'Hot'
      | 'New'
      | 'Frequent trades';
    list_order: number;
  };
  [TradeEvent.TradeCryptoClick]: {
    trade_type: 'perpetual' | 'spot';
    symbol: string;
    carousel_type?: string;
    list_order?: number;
    search_type?:
      | 'All'
      | 'Perpetual'
      | 'Spot'
      | 'Favorites'
      | 'TradFi'
      | 'Positions opened'
      | 'Hot'
      | 'New'
      | 'Frequent trades'
      | null;
    search_word?: string | null;
    price_change_percentage?: number | null;
    event_location?: 'market_tab' | 'discover_tab' | 'zero_spread';
  };
  [TradeEvent.FavoriteClick]: {
    trade_type: 'perpetual' | 'spot';
    symbol: string;
    icon_location: 'title' | 'search';
    is_on_off: boolean;
  };
  [TradeEvent.EmptySearchResultPageView]: {
    search_word: string | null;
    search_type: string;
  };
  [TradeEvent.SelectPercentageClick]: {
    trade_type: 'perpetual' | 'spot';
    symbol: string;
    percentage: number;
    percentage_type: 'trigger_price' | 'tpsl_price' | 'order_amount' | 'adjust_funds';
  };
  [TradeEvent.OrderFormUnitSwitchClick]: {
    trade_type: 'perpetual' | 'spot';
    symbol: string;
    position_mode: 'oneway' | 'multiposition';
    order_by_token: 'position_size' | 'margin' | 'quantity';
    order_type: 'market_order' | 'trigger_price_order' | 'trigger_time_order' | 'limit_order';
  };
  [TradeEvent.OrderAmountEditClick]: {
    symbol: string;
    position_mode: 'oneway' | 'multiposition' | null; // for spot
    order_by_token: 'position_size' | 'margin' | 'quantity';
    amount_input_type: 'manual' | 'slider';
    order_type: 'market_order' | 'trigger_price_order' | 'trigger_time_order' | 'limit_order';
  };
  [TradeEvent.OrderBboClick]: {
    symbol: string;
    checkbox_click: boolean;
  };
  [TradeEvent.AddPositionMethodSelectClick]: {
    symbol: string;
    side: 'buy' | 'sell';
    leverage: number;
    order_by_token: 'position_size' | 'margin' | 'quantity';
  };
  [TradeEvent.AddPositionReviewClick]: {
    symbol: string;
    side: 'buy' | 'sell';
    leverage: number;
    order_by_token: 'position_size' | 'margin' | 'quantity';
    add_amount: number;
    order_type: 'market' | 'limit';
    limit_price: number | null;
    bbo_price: boolean;
  };
  [TradeEvent.AddPositionConfirmClick]: {
    symbol: string;
    side: 'buy' | 'sell';
    leverage: number;
    order_by_token: 'position_size' | 'margin' | 'quantity';
    estimated_position_amount: number;
    existing_position_amount: number;
    estimated_liquidation_price: number;
    existing_liquidation_price: number;
    estimated_entry_price: number;
    existing_entry_price: number;
  };
  [TradeEvent.OrderBookUnitDropdownClick]: {
    symbol: string;
    current_unit: 'QUOTE' | 'BASE';
  };
  [TradeEvent.OrderBookUnitSelectClick]: {
    symbol: string;
    previous_unit: 'QUOTE' | 'BASE';
    unit_selected: 'QUOTE' | 'BASE';
    trade_type: 'perpetual' | 'spot';
  };
  [TradeEvent.OrderFormErrorMsgView]: {
    error_message: string;
    error_case: 'open_position' | 'add_position' | 'remove_funds' | 'adjust_leverage';
    is_recommendation_chip_visible: boolean;
    symbol: string;
    leverage: number;
    order_by_token: 'position_size' | 'margin' | 'quantity' | null;
  };
  [TradeEvent.OrderFormErrorMsgClick]: {
    error_message: string;
    error_case: 'open_position' | 'add_position' | 'remove_funds' | 'adjust_leverage';
    symbol: string;
    leverage: number;
    order_by_token: 'position_size' | 'margin' | 'quantity' | null;
  };
  [TradeEvent.OrderFormHistoryCurrentMarketClick]: {
    toggle_on: boolean;
    execute_stage: 'Position' | 'Order' | 'Fill';
    symbol: string;
  };
  [TradeEvent.OrderFormHistoryCalendarClick]: {
    symbol: string;
  };
}
